import { NavLink, useNavigate, useLocation } from 'react-router-dom';
import { useState, useEffect } from 'react';
import { collection, query, where, onSnapshot } from 'firebase/firestore';
import { db } from '../../lib/firebase';
import { useAuth } from '../../contexts/AuthContext';
import { Button } from '../ui/button';
import { ScrollArea } from '../ui/scroll-area';
import {
  LayoutDashboard,
  Users,
  Calendar,
  Wallet,
  BookOpen,
  Settings,
  LogOut,
  Star,
  FileText,
  Bell,
  Link as LinkIcon,
  GraduationCap,
  Megaphone,
  Activity,
  ShieldCheck,
  ChevronDown,
  ChevronRight,
  Moon,
  MessagesSquare,
  BookOpenCheck
} from 'lucide-react';
import logo from '../../assets/images/logo.jpg';

export const Sidebar = ({ isOpen, onClose }) => {
  const { user, userData, logout, isAdmin, isResponsable } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [unreadCount, setUnreadCount] = useState(0);
  const [isAdminOpen, setIsAdminOpen] = useState(false);



  useEffect(() => {
    if (!user) return;

    const q = query(
      collection(db, 'notifications'),
      where('userId', '==', user.uid),
      where('read', '==', false)
    );

    const unsubscribe = onSnapshot(q, (snapshot) => {
      setUnreadCount(snapshot.size);
    });

    return () => unsubscribe();
  }, [user]);

  const handleLogout = async () => {
    await logout();
    navigate('/');
  };

  const handleLinkClick = () => {
    if (window.innerWidth < 768 && onClose) {
      onClose();
    }
  };

  const memberLinks = [
    { to: '/dashboard', icon: LayoutDashboard, label: 'Tableau de bord' },
    { to: '/dashboard/spiritualite', icon: Moon, label: 'Spiritualité' },
    { to: '/dashboard/lectures', icon: BookOpenCheck, label: 'Lectures collectives' },
    { to: '/dashboard/events', icon: Calendar, label: 'Événements' },
    { to: '/dashboard/communaute', icon: MessagesSquare, label: 'Communauté' },
    { to: '/dashboard/contributions', icon: Wallet, label: 'Cotisations' },
    { to: '/dashboard/mediatheque', icon: BookOpen, label: 'Médiathèque' },
    { to: '/dashboard/learning', icon: GraduationCap, label: 'Espace Culte' },
    { to: '/dashboard/social-links', icon: LinkIcon, label: 'Réseaux sociaux' },
    { to: '/dashboard/notifications', icon: Bell, label: 'Notifications', badge: unreadCount },
  ];

  const adminLinks = [
    { to: '/dashboard/members', icon: Users, label: 'Membres' },
    { to: '/dashboard/finances', icon: Wallet, label: 'Finances' },
    { to: '/dashboard/admin-notifications', icon: Megaphone, label: 'Diffusion' },
    { to: '/dashboard/documents', icon: FileText, label: 'Documents' },
    { to: '/dashboard/audit', icon: Activity, label: 'Audit' },
    { to: '/dashboard/settings', icon: Settings, label: 'Paramètres' },
  ];

  return (
    <>
      {/* Mobile Overlay */}
      {isOpen && (
        <div
          className="fixed inset-0 bg-black/50 z-40 md:hidden"
          onClick={onClose}
          aria-hidden="true"
        />
      )}

      <aside className={`
        fixed left-0 top-0 h-screen w-64 bg-slate-900 border-r border-slate-800 text-slate-100 flex flex-col z-50
        transition-transform duration-300 ease-in-out shadow-2xl
        md:translate-x-0
        ${isOpen ? 'translate-x-0' : '-translate-x-full'}
      `}>
        {/* Logo */}
        <div className="p-6 border-b border-slate-800 flex items-center justify-between bg-slate-900/50 backdrop-blur-sm">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-xl bg-white p-0.5 flex items-center justify-center overflow-hidden shadow-lg shadow-indigo-500/20">
              <img src={logo} alt="Logo" className="w-full h-full object-cover rounded-lg" />
            </div>
            <div>
              <h1 className="font-bold text-lg leading-tight tracking-tight">Daara Wakana</h1>
              <p className="text-[10px] font-medium text-slate-400 uppercase tracking-widest">Haqqan</p>
            </div>
          </div>
        </div>

        {/* Navigation */}
        <ScrollArea className="flex-1 py-6">
          <nav className="px-4 space-y-1.5">
            <p className="px-3 py-2 text-xs font-bold text-slate-500 uppercase tracking-wider mb-2">
              Menu Principal
            </p>
            {memberLinks.map((link) => (
              <NavLink
                key={link.to}
                to={link.to}
                onClick={handleLinkClick}
                end={link.to === '/dashboard'}
                className={({ isActive }) =>
                  `flex items-center gap-3 px-3 py-2.5 rounded-xl text-sm font-medium transition-all duration-200 group relative ${isActive
                    ? 'bg-indigo-600 text-white shadow-lg shadow-indigo-600/25'
                    : 'text-slate-400 hover:bg-slate-800 hover:text-white'
                  }`
                }
              >
                <div className="relative flex items-center justify-center">
                  <link.icon className={`h-5 w-5 transition-transform group-hover:scale-110`} />
                  {link.badge > 0 && (
                    <span className="absolute -top-1.5 -right-1.5 flex h-4 w-4 items-center justify-center rounded-full bg-rose-500 text-[10px] font-bold text-white ring-2 ring-slate-900 animate-in zoom-in">
                      {link.badge > 9 ? '9+' : link.badge}
                    </span>
                  )}
                </div>
                <span>{link.label}</span>
              </NavLink>
            ))}

            {(isAdmin || isResponsable) && (
              <>
                <div className="pt-6 pb-2">
                  <div className="h-px bg-slate-800 mx-3" />
                </div>

                <p className="px-3 py-2 text-xs font-bold text-slate-500 uppercase tracking-wider mb-2">
                  Gestion
                </p>

                {/* Collapsible Admin Menu */}
                <div className="space-y-1">
                  <button
                    onClick={() => setIsAdminOpen(!isAdminOpen)}
                    className={`w-full flex items-center justify-between gap-3 px-3 py-2.5 rounded-xl text-sm font-medium transition-all duration-200 group ${isAdminOpen
                      ? 'bg-slate-800 text-white'
                      : 'text-slate-400 hover:bg-slate-800 hover:text-white'
                      }`}
                  >
                    <div className="flex items-center gap-3">
                      <ShieldCheck className={`h-5 w-5 ${isAdminOpen ? 'text-indigo-400' : 'text-slate-500 group-hover:text-indigo-400'}`} />
                      <span>Administration</span>
                    </div>
                    {isAdminOpen ? (
                      <ChevronDown className="h-4 w-4 text-slate-500" />
                    ) : (
                      <ChevronRight className="h-4 w-4 text-slate-500" />
                    )}
                  </button>

                  <div className={`space-y-1 overflow-hidden transition-all duration-300 ease-in-out ${isAdminOpen ? 'max-h-[500px] opacity-100 mt-1' : 'max-h-0 opacity-0'}`}>
                    {adminLinks.map((link) => (
                      <NavLink
                        key={link.to}
                        to={link.to}
                        onClick={handleLinkClick}
                        className={({ isActive }) =>
                          `flex items-center gap-3 px-3 py-2 ml-4 border-l-2 text-sm font-medium transition-all duration-200 ${isActive
                            ? 'border-indigo-500 text-indigo-400'
                            : 'border-slate-700 text-slate-500 hover:text-slate-300 hover:border-slate-500'
                          }`
                        }
                      >
                        <link.icon className="h-4 w-4" />
                        <span>{link.label}</span>
                      </NavLink>
                    ))}
                  </div>
                </div>
              </>
            )}
          </nav>
        </ScrollArea>

        {/* User Info & Logout */}
        <div className="p-4 border-t border-white/10">
          <div className="flex items-center gap-3 mb-3">
            <div className="w-10 h-10 rounded-full bg-secondary/20 flex items-center justify-center">
              <span className="text-secondary font-semibold">
                {userData?.fullName?.charAt(0) || 'U'}
              </span>
            </div>
            <div className="flex-1 min-w-0">
              <p className="text-sm font-medium truncate">{userData?.fullName || 'Utilisateur'}</p>
              <p className="text-xs text-white/60 capitalize">{userData?.role || 'Membre'}</p>
            </div>
          </div>
          <Button
            variant="ghost"
            className="w-full justify-start text-white/70 hover:text-white hover:bg-white/10"
            onClick={handleLogout}
            data-testid="logout-btn"
          >
            <LogOut className="h-4 w-4 mr-2" />
            Déconnexion
          </Button>
        </div>
      </aside>
    </>
  );
};
